import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { showToast, handleAPIError } from '@/utils/toasts';
import Modal from '@/components/ui/Modal';
import type { UserID, SubscriptionType } from '@/models';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';

interface CreateExternalSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: UserID;
  onCreated: () => void;
}

const STRIPE_SUB_ID_REGEX = /^sub_[a-zA-Z0-9]+$/;

export default function CreateExternalSubscriptionModal({
  isOpen,
  onClose,
  userId,
  onCreated
}: CreateExternalSubscriptionModalProps) {
  const { products, getTypeLabel } = useSubscriptionProducts();
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType | ''>('');
  const [stripeSubscriptionID, setStripeSubscriptionID] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    subscriptionType?: string;
    stripeSubscriptionID?: string;
  }>({});

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (subscriptionType === '') {
      newErrors.subscriptionType = 'Bitte wählen Sie einen Abo-Typ aus';
    }

    if (stripeSubscriptionID.trim() === '') {
      newErrors.stripeSubscriptionID = 'Bitte geben Sie die Stripe Subscription ID ein';
    } else if (!STRIPE_SUB_ID_REGEX.test(stripeSubscriptionID.trim())) {
      newErrors.stripeSubscriptionID = 'Die Stripe Subscription ID muss mit "sub_" beginnen und nur alphanumerische Zeichen enthalten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    if (subscriptionType === '') {
      return;
    }

    setIsLoading(true);
    try {
      await APIRoutes.createActiveSubscription(userId, {
        subscriptionType: subscriptionType,
        stripeSubscriptionID: stripeSubscriptionID.trim()
      });
      showToast('Externes Abonnement erfolgreich erstellt', 'success');
      onCreated();
      handleClose();
    } catch (error) {
      handleAPIError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSubscriptionType('');
      setStripeSubscriptionID('');
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Externes Abo hinzufügen"
      icon={<FontAwesomeIcon icon={faPlus} className="w-5 h-5 text-green-600" />}
      maxWidth="md"
      closeDisabled={isLoading}
    >
      <div className="p-6">
        <div className="space-y-4">
          {/* Subscription Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Abo-Typ <span className="text-red-500">*</span>
            </label>
            <select
              value={subscriptionType}
              onChange={(e) => {
                setSubscriptionType(e.target.value as SubscriptionType | '');
                setErrors({ ...errors, subscriptionType: undefined });
              }}
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer ${
                errors.subscriptionType !== undefined
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <option value="">-- Abo-Typ auswählen --</option>
              {products.map((product) => (
                <option key={product.type} value={product.type}>
                  {product.type} - {getTypeLabel(product.type)}
                </option>
              ))}
            </select>
            {errors.subscriptionType !== undefined && (
              <p className="mt-1 text-sm text-red-500">{errors.subscriptionType}</p>
            )}
          </div>

          {/* Stripe Subscription ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Stripe Subscription ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={stripeSubscriptionID}
              onChange={(e) => {
                setStripeSubscriptionID(e.target.value);
                setErrors({ ...errors, stripeSubscriptionID: undefined });
              }}
              disabled={isLoading}
              placeholder="sub_..."
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                errors.stripeSubscriptionID !== undefined
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {errors.stripeSubscriptionID !== undefined && (
              <p className="mt-1 text-sm text-red-500">{errors.stripeSubscriptionID}</p>
            )}
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Die Stripe Subscription ID beginnt mit "sub_"
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {isLoading && <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />}
            Erstellen
          </button>
        </div>
      </div>
    </Modal>
  );
}
